import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragHandle as DragHandleIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import toast from 'react-hot-toast';
import * as lookupApi from '../../api/lookups';
import * as subServiceApi from '../../api/subServices';
import { Lookup, LookupType, CreateLookupDTO } from '../../types/lookup.types';
import { SubService, UtilityType } from '../../types/utility.types';

const lookupTypes: LookupType[] = [
  'MeterType',
  'PropertyType',
  'ServiceType',
  'UtilityServiceStatus',
  'LeadDropReason',
  'Industry',
  'Partners',
  'Positions',
  'QuoteRejectionReason',
  'SubBrokerLevel'
];

// Add a special tab for Utility Sub-Services
const UTILITY_SUB_SERVICES_TAB = 'UtilitySubServices';

// Convert lookup types to display names for tabs
const lookupTabs = [
  'Meter Type',
  'Property Type',
  'Service Type',
  'Utility Service Status',
  'Lead Drop Reason',
  'Industry',
  'Partners',
  'Positions',
  'Quote Rejection Reason',
  'Sub Broker Level'
];

// Function to format lookup type names
const formatLookupType = (type: LookupType): string => {
  // Split on capital letters and join with space
  return type.replace(/([A-Z])/g, ' $1').trim();
};

const Lookups = () => {
  // Use the formatted tab name as the selected tab state
  const [selectedTab, setSelectedTab] = useState('Meter Type');
  const [lookups, setLookups] = useState<{ [key in LookupType]: Lookup[] }>(lookupTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}) as { [key in LookupType]: Lookup[] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLookup, setEditingLookup] = useState<Lookup | null>(null);
  const [formData, setFormData] = useState<CreateLookupDTO>({
    type: 'MeterType',
    value: '',
    description: '',
    active: true
  });
  const [metadataFields, setMetadataFields] = useState({
    commissionRate: '',
    brokerSplit: '',
    subBrokerSplit: '',
    level: ''
  });

  // Utility Sub-Services state
  const [subServices, setSubServices] = useState<SubService[]>([]);
  const [subServiceDialogOpen, setSubServiceDialogOpen] = useState(false);
  const [editingSubService, setEditingSubService] = useState<SubService | null>(null);
  const [subServiceFormData, setSubServiceFormData] = useState<{
    name: string;
    utilityType: UtilityType;
    description: string;
    isActive: boolean;
  }>({
    name: '',
    utilityType: 'Electricity',
    description: '',
    isActive: true
  });

  useEffect(() => {
    const fetchData = async () => {
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch lookups
        
        const allLookups = await lookupApi.getAllLookups();
        
        
        // Initialize lookup groups with all types
        
        const lookupsByType = lookupTypes.reduce((acc, type) => {
          acc[type] = [];
          return acc;
        }, {} as { [key in LookupType]: Lookup[] });

        // Group lookups by type
        
        allLookups.forEach(lookup => {
          if (lookup.type in lookupsByType) {
            lookupsByType[lookup.type as LookupType].push(lookup);
            
          }
        });

        // Log the final state of each type
        lookupTypes.forEach(type => {
          
        });
        
        
        setLookups(lookupsByType);
        
        // Fetch utility sub-services
        try {
          // Use the API function instead of fetch directly
          const subServicesData = await subServiceApi.getAllSubServices();
          // Ensure we always have an array, even if the API returns null or undefined
          setSubServices(Array.isArray(subServicesData) ? subServicesData : []);
        } catch (subServiceErr) {
          console.error('Error fetching sub-services:', subServiceErr);
          // Don't fail the whole page if sub-services fail to load
          setSubServices([]);
        }
        
      } catch (err: any) {
        /* removed console.error */
        setError(err.message || 'Failed to load lookups');
        toast.error('Failed to load lookups. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Debug log the available lookup types
  useEffect(() => {
    
    
  }, [lookups]);

  
  // Handlers for utility sub-services
  const handleOpenSubServiceDialog = (subService?: SubService) => {
    if (subService) {
      setEditingSubService(subService);
      setSubServiceFormData({
        name: subService.name,
        utilityType: subService.utilityType,
        description: subService.description || '',
        isActive: subService.isActive
      });
    } else {
      setEditingSubService(null);
      setSubServiceFormData({
        name: '',
        utilityType: 'Electricity',
        description: '',
        isActive: true
      });
    }
    setSubServiceDialogOpen(true);
  };
  
  const handleCloseSubServiceDialog = () => {
    setSubServiceDialogOpen(false);
  };
  
  const handleSubServiceFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    if (name) {
      setSubServiceFormData(prev => ({
        ...prev,
        [name]: name === 'isActive' ? (value === 'true') : value
      }));
    }
  };
  
  const handleSaveSubService = async () => {
    try {
      // Validate form data
      if (!subServiceFormData.name || !subServiceFormData.utilityType) {
        toast.error('Name and Utility Type are required');
        return;
      }

      setLoading(true);

      if (editingSubService) {
        // Update existing sub-service
        await subServiceApi.updateSubService(editingSubService._id, {
          ...subServiceFormData,
          isActive: subServiceFormData.isActive
        });
        
        // Update local state
        setSubServices(prev => {
          if (!Array.isArray(prev)) return []; // Safety check
          return prev.map(item => 
            item._id === editingSubService._id ? { ...item, ...subServiceFormData } : item
          );
        });
        
        toast.success('Sub-service updated successfully');
      } else {
        // Create new sub-service
        const newSubService = await subServiceApi.createSubService(subServiceFormData);
        
        // Add to local state
        setSubServices(prev => {
          if (!Array.isArray(prev)) return [newSubService]; // Safety check
          return [...prev, newSubService];
        });
        
        toast.success('Sub-service added successfully');
      }

      // Close dialog and reset form
      handleCloseSubServiceDialog();
    } catch (err) {
      console.error('Failed to save sub-service:', err);
      toast.error('Failed to save sub-service');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleSubServiceActive = async (subService: SubService) => {
    try {
      await subServiceApi.updateSubService(subService._id, {
        isActive: !subService.isActive
      });
      
      // Update local state
      setSubServices(prev => {
        if (!Array.isArray(prev)) return []; // Safety check
        return prev.map(item => 
          item._id === subService._id 
            ? { ...item, isActive: !item.isActive } 
            : item
        );
      });
      
      toast.success(`Sub-service ${subService.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error: any) {
      
      toast.error('Failed to update sub-service status');
    }
  };
  
  const handleDeleteSubService = async (subService: SubService) => {
    if (!window.confirm(`Are you sure you want to delete the sub-service "${subService.name}"?`)) {
      return;
    }
    
    try {
      // Delete sub-service
      await subServiceApi.deleteSubService(subService._id);
      
      // Update local state
      setSubServices(prev => {
        if (!Array.isArray(prev)) return []; // Safety check
        return prev.filter(item => item._id !== subService._id);
      });
      
      toast.success('Sub-service deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete sub-service:', err);
      
      // Check if the error is because the sub-service is in use
      if (err.response && err.response.data && err.response.data.message) {
        if (err.response.data.message.includes('being used')) {
          const usedByCount = err.response.data.usedBy || 'multiple';
          toast.error(`Cannot delete this sub-service because it is being used by ${usedByCount} utility service(s)`);
          return;
        }
      }
      
      // Generic error message if not a specific case
      toast.error('Failed to delete sub-service');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}
          <Button 
            color="inherit" 
            size="small" 
            onClick={() => window.location.reload()}
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  const handleAddClick = () => {
    // Get the current lookup type based on the selected tab
    const tabIndex = lookupTabs.findIndex(tab => tab === selectedTab);
    
    setEditingLookup(null);
    setFormData({
      type: lookupTypes[tabIndex],
      value: '',
      description: '',
      active: true
    });
    setMetadataFields({
      commissionRate: '',
      brokerSplit: '',
      subBrokerSplit: '',
      level: ''
    });
    setDialogOpen(true);
  };

  const handleEditClick = (lookup: Lookup) => {
    setEditingLookup(lookup);
    setFormData({
      type: lookup.type as LookupType,
      value: lookup.value,
      description: lookup.description || '',
      active: lookup.active
    });
    
    // Load metadata if it's a SubBrokerLevel
    if (lookup.type === 'SubBrokerLevel' && lookup.metadata) {
      setMetadataFields({
        commissionRate: lookup.metadata.commissionRate?.toString() || '',
        brokerSplit: lookup.metadata.brokerSplit?.toString() || '',
        subBrokerSplit: lookup.metadata.subBrokerSplit?.toString() || '',
        level: lookup.metadata.level?.toString() || ''
      });
    } else {
      setMetadataFields({
        commissionRate: '',
        brokerSplit: '',
        subBrokerSplit: '',
        level: ''
      });
    }
    
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingLookup(null);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare the data with metadata if it's a SubBrokerLevel
      const dataToSubmit = { ...formData };
      if (formData.type === 'SubBrokerLevel') {
        dataToSubmit.metadata = {
          commissionRate: Number(metadataFields.commissionRate) || 0,
          brokerSplit: Number(metadataFields.brokerSplit) || 0,
          subBrokerSplit: Number(metadataFields.subBrokerSplit) || 0,
          level: Number(metadataFields.level) || 0
        };
      }
      
      if (editingLookup) {
        await lookupApi.updateLookup(editingLookup._id, dataToSubmit);
        toast.success('Lookup updated successfully');
      } else {
        await lookupApi.createLookup(dataToSubmit);
        toast.success('Lookup created successfully');
      }
      handleDialogClose();
      const fetchLookups = async () => {
        try {
          setLoading(true);
          setError(null);
          const allLookups = await lookupApi.getAllLookups();
          
          // Group lookups by type
          const lookupsByType = lookupTypes.reduce((acc, type) => {
            acc[type] = allLookups.filter(lookup => lookup.type === type);
            return acc;
          }, {} as { [key in LookupType]: Lookup[] });
          
          setLookups(lookupsByType);
        } catch (err: any) {
          
          setError(err.message || 'Failed to load lookups');
          toast.error('Failed to load lookups. Please try again later.');
        } finally {
          setLoading(false);
        }
      };

      fetchLookups();
    } catch (err) {
      
      toast.error('Failed to save lookup');
    }
  };

  const handleToggleStatus = async (lookup: Lookup) => {
    try {
      await lookupApi.toggleLookupStatus(lookup._id);
      toast.success('Status updated successfully');
      const fetchLookups = async () => {
        try {
          setLoading(true);
          setError(null);
          const allLookups = await lookupApi.getAllLookups();
          
          // Group lookups by type
          const lookupsByType = lookupTypes.reduce((acc, type) => {
            acc[type] = allLookups.filter(lookup => lookup.type === type);
            return acc;
          }, {} as { [key in LookupType]: Lookup[] });
          
          setLookups(lookupsByType);
        } catch (err: any) {
          
          setError(err.message || 'Failed to load lookups');
          toast.error('Failed to load lookups. Please try again later.');
        } finally {
          setLoading(false);
        }
      };

      fetchLookups();
    } catch (err) {
      
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (lookup: Lookup) => {
    if (!window.confirm('Are you sure you want to delete this lookup?')) {
      return;
    }

    try {
      await lookupApi.deleteLookup(lookup._id);
      toast.success('Lookup deleted successfully');
      const fetchLookups = async () => {
        try {
          setLoading(true);
          setError(null);
          const allLookups = await lookupApi.getAllLookups();
          
          // Group lookups by type
          const lookupsByType = lookupTypes.reduce((acc, type) => {
            acc[type] = allLookups.filter(lookup => lookup.type === type);
            return acc;
          }, {} as { [key in LookupType]: Lookup[] });
          
          setLookups(lookupsByType);
        } catch (err: any) {
          
          setError(err.message || 'Failed to load lookups');
          toast.error('Failed to load lookups. Please try again later.');
        } finally {
          setLoading(false);
        }
      };

      fetchLookups();
    } catch (err) {
      
      toast.error('Failed to delete lookup');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    // Get the current lookup type based on the selected tab
    const tabIndex = lookupTabs.findIndex(tab => tab === selectedTab);
    const type = lookupTypes[tabIndex];
    const items = Array.from(lookups[type]);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UI
    setLookups({
      ...lookups,
      [type]: items
    });

    try {
      await lookupApi.reorderLookups(
        type,
        items.map(item => item._id)
      );
    } catch (err) {
      
      toast.error('Failed to save new order');
      const fetchLookups = async () => {
        try {
          setLoading(true);
          setError(null);
          const allLookups = await lookupApi.getAllLookups();
          
          // Group lookups by type
          const lookupsByType = lookupTypes.reduce((acc, type) => {
            acc[type] = allLookups.filter(lookup => lookup.type === type);
            return acc;
          }, {} as { [key in LookupType]: Lookup[] });
          
          setLookups(lookupsByType);
        } catch (err: any) {
          
          setError(err.message || 'Failed to load lookups');
          toast.error('Failed to load lookups. Please try again later.');
        } finally {
          setLoading(false);
        }
      };

      fetchLookups();
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          background: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'white' }}>
            Admin Lookups
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={4}>
          <ToggleButtonGroup
            value={selectedTab}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setSelectedTab(newValue);
              }
            }}
            aria-label="lookup categories"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 3,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px !important',
                m: 0.5,
                '&.Mui-selected': {
                  background: 'rgba(58, 123, 213, 0.15)',
                  borderColor: '#3a7bd5',
                  color: '#3a7bd5'
                },
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.05)'
                }
              }
            }}
          >
          {lookupTypes.map((type) => (
            <ToggleButton 
              key={type} 
              value={formatLookupType(type)}
              sx={{
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': {
                  color: '#3a7bd5'
                }
              }}
            >
              {formatLookupType(type)}
            </ToggleButton>
          ))}
          <ToggleButton 
            key={UTILITY_SUB_SERVICES_TAB} 
            value="Utility Sub-Services"
            sx={{
              px: 2,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: '#3a7bd5'
              }
            }}
          >
            Utility Sub-Services
          </ToggleButton>
        </ToggleButtonGroup>
        </Box>

        <Box p={3}>
          {/* Show different content based on the selected tab */}
          {selectedTab !== 'Utility Sub-Services' ? (
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    background: 'linear-gradient(90deg, #3a7bd5, #00d2ff)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #3a7bd5, #3a7bd5)'
                    }
                  }}
                >
                  Add {selectedTab}
                </Button>
              </Box>

              <DragDropContext onDragEnd={handleDragEnd}>
                {/* Get the lookup type based on the selected tab */}
                <Droppable droppableId={lookupTypes[lookupTabs.findIndex(tab => tab === selectedTab)]}>
                  {(provided: any) => (
                    <List 
                      {...provided.droppableProps} 
                      ref={provided.innerRef}
                      sx={{
                        bgcolor: 'transparent',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      {lookups[lookupTypes[lookupTabs.findIndex(tab => tab === selectedTab)]]?.map((lookup, index) => (
                    <Draggable key={lookup._id} draggableId={lookup._id} index={index}>
                      {(provided) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          divider
                        >
                          <div {...provided.dragHandleProps}>
                            <DragHandleIcon sx={{ mr: 2, color: 'text.secondary' }} />
                          </div>
                          <ListItemText
                            primary={lookup.value}
                            secondary={lookup.description}
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={lookup.active}
                              onChange={() => handleToggleStatus(lookup)}
                            />
                            <Tooltip title="Edit">
                              <IconButton
                                edge="end"
                                onClick={() => handleEditClick(lookup)}
                                sx={{ ml: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                edge="end"
                                onClick={() => handleDelete(lookup)}
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          ) : (
            // Utility Sub-Services Tab Content
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenSubServiceDialog()}
                >
                  Add Utility Sub-Service
                </Button>
              </Box>
              
              <List>
                {Array.isArray(subServices) && subServices.map((subService) => (
                  <ListItem key={subService._id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" component="span">
                            {subService.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            component="span" 
                            sx={{ ml: 2, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'primary.light', color: 'white' }}
                          >
                            {subService.utilityType}
                          </Typography>
                        </Box>
                      }
                      secondary={subService.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={subService.isActive}
                        onChange={() => handleToggleSubServiceActive(subService)}
                      />
                      <Tooltip title="Edit">
                        <IconButton
                          edge="end"
                          onClick={() => handleOpenSubServiceDialog(subService)}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteSubService(subService)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {subServices.length === 0 && (
                  <Box p={3} textAlign="center">
                    <Typography variant="body1" color="textSecondary">
                      No utility sub-services found. Click the Add button to create one.
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          )}
        </Box>
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(18, 18, 18, 0.8)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
          }
        }}
      >
        <form onSubmit={handleFormSubmit}>
          <DialogTitle sx={{ color: 'white' }}>
            {editingLookup ? 'Edit Lookup' : 'Add New Lookup'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Value"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.4)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3a7bd5'
                  },
                  color: 'white'
                }
              }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.4)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3a7bd5'
                  },
                  color: 'white'
                }
              }}
            />
            
            {/* Show metadata fields only for SubBrokerLevel */}
            {formData.type === 'SubBrokerLevel' && (
              <>
                <TextField
                  fullWidth
                  label="Commission Rate (%)"
                  type="number"
                  value={metadataFields.commissionRate}
                  onChange={(e) => setMetadataFields({ ...metadataFields, commissionRate: e.target.value })}
                  margin="normal"
                  required
                  InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.23)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.4)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3a7bd5'
                      },
                      color: 'white'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Broker Split (%)"
                    type="number"
                    value={metadataFields.brokerSplit}
                    onChange={(e) => setMetadataFields({ ...metadataFields, brokerSplit: e.target.value })}
                    margin="normal"
                    required
                    InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                    InputProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.23)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.4)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3a7bd5'
                        },
                        color: 'white'
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Sub-Broker Split (%)"
                    type="number"
                    value={metadataFields.subBrokerSplit}
                    onChange={(e) => setMetadataFields({ ...metadataFields, subBrokerSplit: e.target.value })}
                    margin="normal"
                    required
                    InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                    InputProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.23)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.4)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3a7bd5'
                        },
                        color: 'white'
                      }
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Level (1, 2, 3)"
                  type="number"
                  value={metadataFields.level}
                  onChange={(e) => setMetadataFields({ ...metadataFields, level: e.target.value })}
                  margin="normal"
                  required
                  InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.23)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.4)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3a7bd5'
                      },
                      color: 'white'
                    }
                  }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleDialogClose}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  color: 'white'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                background: 'linear-gradient(90deg, #3a7bd5, #00d2ff)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #3a7bd5, #3a7bd5)'
                }
              }}
            >
              {editingLookup ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog for adding/editing utility sub-services */}
      <Dialog 
        open={subServiceDialogOpen} 
        onClose={handleCloseSubServiceDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(18, 18, 18, 0.8)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingSubService ? 'Edit Utility Sub-Service' : 'Add New Utility Sub-Service'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={subServiceFormData.name}
            onChange={handleSubServiceFormChange}
            margin="normal"
            required
            InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
            InputProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.4)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3a7bd5'
                },
                color: 'white'
              }
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="utility-type-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Utility Type</InputLabel>
            <Select
              labelId="utility-type-label"
              name="utilityType"
              value={subServiceFormData.utilityType}
              onChange={handleSubServiceFormChange}
              label="Utility Type"
              required
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.4)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3a7bd5'
                },
                '.MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              <MenuItem value="Gas">Gas</MenuItem>
              <MenuItem value="Electricity">Electricity</MenuItem>
              <MenuItem value="Water">Water</MenuItem>
              <MenuItem value="Telecoms">Telecoms</MenuItem>
              <MenuItem value="Waste">Waste</MenuItem>
              <MenuItem value="Merchant Services">Merchant Services</MenuItem>
              <MenuItem value="PPA Export Contract">PPA Export Contract</MenuItem>
              <MenuItem value="EV">EV</MenuItem>
              <MenuItem value="Renewable">Renewable</MenuItem>
              <MenuItem value="Insurance">Insurance</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={subServiceFormData.description}
            onChange={handleSubServiceFormChange}
            margin="normal"
            multiline
            rows={2}
            InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
            InputProps={{
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.4)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3a7bd5'
                },
                color: 'white'
              }
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="active-status-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
            <Select
              labelId="active-status-label"
              name="isActive"
              value={subServiceFormData.isActive ? 'true' : 'false'}
              onChange={handleSubServiceFormChange}
              label="Status"
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.4)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3a7bd5'
                },
                '.MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
            <FormHelperText sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Active sub-services will be available for selection when creating utility services
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseSubServiceDialog}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSubService} 
            variant="contained" 
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              background: 'linear-gradient(90deg, #3a7bd5, #00d2ff)',
              '&:hover': {
                background: 'linear-gradient(90deg, #3a7bd5, #3a7bd5)'
              }
            }}
          >
            {editingSubService ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Lookups;
